import { Link } from "react-router-dom";
import { MessageCircle, Instagram, Globe, MapPin, Clock } from "lucide-react";
import { CONTACT, IMAGES } from "../data/products";

const Page = ({ kicker, title, intro, children, testId }) => (
    <div className="min-h-screen bg-ivory pt-32 pb-28 px-6 sm:px-10" data-testid={testId}>
        <div className="max-w-3xl mx-auto">
            <p className="text-[10px] tracking-[0.3em] uppercase text-golddark mb-3">{kicker}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-6">{title}</h1>
            {intro && <p className="text-base text-smoke leading-relaxed mb-14 max-w-2xl">{intro}</p>}
            <div className="prose-az space-y-10">{children}</div>
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <section>
        <h2 className="font-serif text-2xl text-charcoal mb-3">{title}</h2>
        <div className="text-sm text-smoke leading-relaxed space-y-3">{children}</div>
    </section>
);

const updated = "Last updated: 1 October 2026";

export function AboutPage() {
    return (
        <Page kicker="Maison Al Zahra" title="About us" testId="about-page"
            intro="Al Zahra Perfumery is a small Kolkata house devoted to alcohol-free attar — fragrant oils worn on the skin the way they have been for centuries across Arabia, Persia and the Indian subcontinent.">
            <div className="grid sm:grid-cols-2 gap-10 items-start">
                <img src={IMAGES.heroArt} alt="Al Zahra attar" className="w-full aspect-[4/5] object-cover border border-gold/30" />
                <div className="space-y-8">
                    <Section title="Our story">
                        <p>Founded by {CONTACT.owner} near Nakhoda Masjid in old Kolkata, Al Zahra began with a single belief: a great scent should be worn close, last long, and never shout. Every attar is formulated in France, poured by hand into 12 ml bottles, and finished with our signature "AZ" seal.</p>
                    </Section>
                    <Section title="What we make">
                        <p><strong className="text-charcoal">The French Series</strong> — contemporary attars with crisp openings and warm, musky bases.</p>
                        <p><strong className="text-charcoal">The Bakhoor Collection</strong> — hand-pressed agarwood chips for the majlis and the home.</p>
                        <p><strong className="text-charcoal">The Arabian Series</strong> — coming soon.</p>
                    </Section>
                    <Section title="Our promise">
                        <p>Alcohol-free, skin-kind, long lasting. Shipped across India and worldwide, with every order confirmed personally over WhatsApp.</p>
                    </Section>
                </div>
            </div>
        </Page>
    );
}

export function ContactPage() {
    const rows = [
        [MessageCircle, "WhatsApp", CONTACT.whatsappDisplay, `https://wa.me/${CONTACT.whatsapp}`, "contact-whatsapp"],
        [Instagram, "Instagram", `@${CONTACT.instagram}`, `https://www.instagram.com/${CONTACT.instagram}/`, "contact-instagram"],
        [Globe, "Website", CONTACT.website, `https://${CONTACT.website}`, "contact-website"],
    ];
    return (
        <Page kicker="Reach the Maison" title="Contact" testId="contact-page"
            intro="Questions about a scent, an order, or a gift? Message us — Saad replies personally, usually within a few hours.">
            <div className="grid sm:grid-cols-2 gap-6">
                {rows.map(([Icon, label, value, href, tid]) => (
                    <a key={label} href={href} target="_blank" rel="noopener noreferrer" data-testid={tid} className="group border border-gold/30 bg-cream/40 p-6 hover:border-gold transition-colors duration-300">
                        <Icon size={18} strokeWidth={1.5} className="text-golddark mb-4" />
                        <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-1">{label}</p>
                        <p className="font-serif text-xl text-charcoal group-hover:text-golddark transition-colors">{value}</p>
                    </a>
                ))}
                <div className="border border-gold/30 bg-cream/40 p-6">
                    <MapPin size={18} strokeWidth={1.5} className="text-golddark mb-4" />
                    <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-1">Atelier</p>
                    <p className="font-serif text-xl text-charcoal">2/A Zakaria Street</p>
                    <p className="text-sm text-smoke">Near Nakhoda Masjid, Kolkata 700073, India</p>
                </div>
            </div>
            <Section title="Hours">
                <p className="flex items-center gap-2"><Clock size={14} className="text-golddark" /> Monday – Saturday, 11:00 – 20:00 IST. Orders placed online are accepted 24/7.</p>
            </Section>
            <Section title="Order help">
                <p>Already ordered? <Link to="/track" className="text-golddark underline underline-offset-4" data-testid="contact-track-link">Track your parcel</Link> with your phone number, or message us your order number on WhatsApp.</p>
            </Section>
        </Page>
    );
}

export function PrivacyPage() {
    return (
        <Page kicker="Legal" title="Privacy Policy" intro={updated} testId="privacy-page">
            <Section title="What we collect">
                <p>When you place an order we collect your name, phone number, delivery address and PIN code, and the items you purchased. Payments are processed by Razorpay or Stripe; we never see or store your card, UPI or bank details.</p>
            </Section>
            <Section title="How we use it">
                <p>Solely to fulfil and deliver your order, to contact you about it on WhatsApp, and to meet our legal and tax obligations. We do not sell or rent your information.</p>
            </Section>
            <Section title="Who we share it with">
                <p>Our payment providers (Razorpay, Stripe) to process payment, and our shipping partner (Shiprocket and the assigned courier) to deliver your parcel. Each receives only what it needs.</p>
            </Section>
            <Section title="Retention & your rights">
                <p>Order records are kept for as long as required by Indian tax law. You may request a copy or deletion of your personal data at any time by messaging us on WhatsApp at {CONTACT.whatsappDisplay}.</p>
            </Section>
            <Section title="Cookies">
                <p>We use only essential browser storage to remember your cart and language. No advertising trackers are used.</p>
            </Section>
        </Page>
    );
}

export function TermsPage() {
    return (
        <Page kicker="Legal" title="Terms & Conditions" intro={updated} testId="terms-page">
            <Section title="About these terms">
                <p>These terms govern purchases from Al Zahra Perfumery ("we", "us") via {CONTACT.website}. By placing an order you agree to them.</p>
            </Section>
            <Section title="Products & pricing">
                <p>All prices are shown in Indian Rupees (with indicative conversions in other currencies) and include applicable taxes. Delivery charges are calculated live at checkout from your PIN code, order weight and payment method. We may correct pricing errors and will contact you before shipping if a price was wrong.</p>
            </Section>
            <Section title="Orders & payment">
                <p>An order is confirmed once payment is verified by our payment provider, or, for Cash on Delivery, once we confirm availability over WhatsApp. We reserve the right to decline or cancel orders where fraud or stock issues are suspected; any payment taken will be refunded in full.</p>
            </Section>
            <Section title="Use of products">
                <p>Attars are concentrated perfume oils for external use on skin or fabric. Bakhoor is for burning on charcoal or an electric burner in a ventilated space. Keep away from children, eyes and open flame.</p>
            </Section>
            <Section title="Liability & law">
                <p>Our liability is limited to the value of your order. These terms are governed by the laws of India; disputes fall under the jurisdiction of the courts of Kolkata, West Bengal.</p>
            </Section>
        </Page>
    );
}

export function ShippingPolicyPage() {
    return (
        <Page kicker="Legal" title="Shipping Policy" intro={updated} testId="shipping-policy-page">
            <Section title="Dispatch">
                <p>Orders are packed in Kolkata and dispatched within 1–2 working days of confirmation. You receive the courier name and tracking number on WhatsApp, and can follow the parcel any time on our <Link to="/track" className="text-golddark underline underline-offset-4">Track my order</Link> page.</p>
            </Section>
            <Section title="Delivery time & charges (India)">
                <p>Metro cities typically 2–4 days; rest of India 4–7 days. Delivery charges are calculated live at checkout from our courier partner (Shiprocket) based on your PIN code, order weight and payment method, so you always see the exact rate before you pay.</p>
            </Section>
            <Section title="International">
                <p>We ship worldwide including the UAE, Europe and Bangladesh. Rates and duties are confirmed over WhatsApp before dispatch; customs duties, if any, are payable by the recipient.</p>
            </Section>
            <Section title="Delays & lost parcels">
                <p>If a parcel is delayed beyond the estimated date or the tracking stops updating, message us — we will raise it with the courier and reship or refund if the parcel is confirmed lost.</p>
            </Section>
        </Page>
    );
}

export function RefundPolicyPage() {
    return (
        <Page kicker="Legal" title="Refund & Return Policy" intro={updated} testId="refund-policy-page">
            <Section title="Damaged or wrong items">
                <p>If your order arrives damaged, leaking or incorrect, send us a photo on WhatsApp within 48 hours of delivery. We will replace it or refund you in full, including shipping — no need to return the damaged item.</p>
            </Section>
            <Section title="Change of mind">
                <p>For hygiene reasons, opened attars and bakhoor cannot be returned. Unopened, sealed items may be returned within 7 days of delivery; return shipping is borne by the customer and a refund is issued once the item is received in original condition.</p>
            </Section>
            <Section title="Cancellations">
                <p>Orders can be cancelled free of charge any time before dispatch by messaging us. Once shipped, the return policy above applies.</p>
            </Section>
            <Section title="How refunds are paid">
                <p>Refunds go back to the original payment method (UPI, card or bank via Razorpay/Stripe) within 5–7 working days of approval. Cash on Delivery orders are refunded by UPI or bank transfer.</p>
            </Section>
        </Page>
    );
}
